/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { useIntl } from 'react-intl';
// Using @compiled/react because @atlaskit/css (and therefore the Pressable primitive's xcss)
// only accepts font tokens, and this inline link must inherit the font of the surrounding
// contributor text wherever the macro is rendered.
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { i18n } from './contributorsMacroI18n.ts';

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		appearance: 'none',
		borderStyle: 'none',
		backgroundColor: 'transparent',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		cursor: 'pointer',
		// Native <button> doesn't inherit font by default; match the surrounding contributor text.
		font: 'inherit',
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('space.025'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
	},
});

export interface ShowMoreButtonProps {
	hiddenCount: number;
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const ShowMoreButton = ({
	hiddenCount,
	onClick,
}: ShowMoreButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const values = { count: hiddenCount };

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-html-button -- Pressable's xcss can't express `font: inherit` (see styles above); focus ring is replicated with ADS tokens.
		<button
			type="button"
			css={styles.root}
			data-testid="contributors-show-more"
			onClick={onClick}
			title={formatMessage(i18n.showMoreTooltip, values)}
		>
			{formatMessage(i18n.showMoreLabel, values)}
		</button>
	);
};
